import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AllocationRowView, SleeveAllocation } from '../../models/reallocation-v2.models';
import {
  REALLOCATION_V2_DO_NOT_TRADE_STEP_ENABLED,
  ReallocationV2Store,
} from '../../state/reallocation-v2.store';

/** Step 5 — read-only summary of the whole request before submitting. */
@Component({
  selector: 'app-rav2-review-submit-step',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './review-submit-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReviewSubmitStepComponent {
  readonly store = inject(ReallocationV2Store);

  /**
   * Whether to show the Do Not Trade column on the Custom Holdings table.
   *
   * Tied to the step's flag rather than to whether anything is marked: with the step off
   * nothing can be, so the column would be a full column of em-dashes for a concept the
   * advisor was never offered -- which reads as a defect rather than as "none selected".
   */
  readonly showDoNotTrade = REALLOCATION_V2_DO_NOT_TRADE_STEP_ENABLED;

  // ---- What moved, per model (ACMTECH-1040) ----------------------------------
  //
  // A sleeve can lose value two ways and they are different instructions: money FREED on
  // Raise Cash, which leaves the models altogether, and money RE-WEIGHTED on Allocate to
  // Models, which moves between them. From the end states alone they are indistinguishable --
  // a sleeve down $10,000 could be either or any mix -- and freeing from a model writes no
  // sell and no released-cash row, so nothing else in the request says which happened.
  //
  // Exact, because the re-seed leaves each sleeve at `current - freed` before step 2 runs:
  //   current - freed + reweighted = requested
  //
  // ⚠️ A SUB-LINE, NOT COLUMNS (AndyP, 2026-10-28). Four value columns were built and taken
  // out again: with three percentage columns already present they overflowed both this table
  // and CRM's, where the headings actually collided. The figures are context for the row, and
  // the sub-line is the device both screens already use for that -- "Total Value" on Allocate
  // to Models, "was <model>" in the tracker.

  /** What the sleeve holds today, or null on a row the advisor added (no sleeve behind it). */
  rowCurrent(row: AllocationRowView): number | null {
    return row.sleeveCurrentValue ?? null;
  }

  /** What Raise Cash took out of it. */
  rowCashRaised(row: AllocationRowView): number {
    if (row.orionConnectAccountId == null) return 0;
    return this.store.cashRaisedByConnectAccountId().get(row.orionConnectAccountId) ?? 0;
  }

  // 🔴 `rowReweighted` WAS HERE AND IS GONE (AndyP, 2026-10-29), with the `signedMoney`
  // formatter that only it used. The figure is still computed and still shown -- in the CRM
  // tracker, where telling a cash raise from a rebalance IS the trade team's work. On the
  // advisor's own review page it was fodder: it is the sleeve returning to its stated target,
  // which they did not ask for and cannot act on, and it appears on an UNTOUCHED request
  // because a drifted account restates itself the moment it loads.

  /** The freed figure for a sleeve being closed, which has no requested row. */
  removedCashRaised(sleeve: SleeveAllocation): number {
    return sleeve.accountId == null
      ? 0
      : this.store.cashRaisedByConnectAccountId().get(sleeve.accountId) ?? 0;
  }

  /** Everything the request raised out of the models, for the one line that states it. */
  cashRaisedTotal(): number {
    return this.store.totalDrawnFromModels();
  }

  money(value: number | null): string {
    if (value == null) return '\u2014';
    return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  /** True when a figure is big enough to be worth a line of its own. */
  moved(value: number): boolean {
    return Math.abs(value) >= 0.005;
  }

  /** "CONTRIBUTION FUNDING" / "DISTRIBUTION FUNDING", for the section header. */
  fundingHeader(): string {
    return this.store.isContribution() ? 'Contribution Funding' : 'Distribution Funding';
  }

  /** How the sleeve money is described — into the models, or out of them. */
  sleeveHeader(): string {
    return this.store.isContribution() ? 'Contribution to Models' : 'Distribution from Models';
  }

  /** The amount row's caption, which is the whole request in one line. */
  amountLabel(): string {
    if (this.store.isCloseAccount()) return 'Full account value';
    return this.store.isContribution() ? 'Contribution amount' : 'Gross amount';
  }

  /**
   * The frequency, plus its start date when it has one.
   *
   * One line rather than two rows: a schedule is a single fact, and "Weekly" with the date on a
   * separate row three lines down reads as two unrelated settings.
   */
  scheduleLabel(): string {
    const frequency = this.store.selectedDeliveryFrequency()?.name ?? '—';
    const start = this.store.fundingForm.controls.startDate.value;
    if (!this.store.startDateRequired() || start == null) return frequency;
    return `${frequency}, starting ${start.toLocaleDateString('en-US')}`;
  }

  /**
   * Where the money lands — the chosen bank account, or the address a cheque is posted to, or
   * why there is neither.
   *
   * ⚠️ "Not applicable" rather than a dash for the methods that use neither: an em-dash on a
   * request that never needed one reads as a value that failed to load.
   *
   * Both come off the same control, because only one of the two is ever asked and the chosen
   * row's `displayValue` is what reaches `DeliveryInstruction` either way. The row label alone is
   * enough here: for a cheque going to an address on file it is the payee's name, and for one
   * going anywhere else the Payee block below carries the address in full.
   */
  /**
   * What to call the destination row — the two cases share one line.
   *
   * ⚠️ The LABEL changes, not the row. A cheque's destination is an address and a transfer's is
   * an account; printing both as "Bank account" would describe a cheque as something it is not,
   * and giving them separate rows would leave one of the two permanently blank on every request.
   */
  destinationLabel(): string {
    return this.store.usesAddressBook() ? 'Sent to' : 'Bank account';
  }

  bankLabel(): string {
    if (!this.store.usesBankAccount() && !this.store.usesAddressBook()) {
      return 'Not applicable for this method';
    }
    return this.store.selectedStandingInstruction()?.displayValue ?? '—';
  }

  /** Withholding as a single line; '—' when neither is set. */
  withholdingLabel(): string {
    const fed = this.store.federalTaxPct();
    const state = this.store.stateTaxPct();
    if (fed === 0 && state === 0) return 'None';
    return `Federal ${fed.toFixed(2)}% · State ${state.toFixed(2)}%`;
  }
}
